// Spec 43 design-system — UI pattern shape contracts.
//
// Shapes (TypeScript types) for the cross-spec UX patterns spec 43
// declares: empty state, loading state, toast / alert, form field.
// Each shape is the MINIMUM contract a component must satisfy when
// it implements that pattern; component authors in Phase 4b extend
// with their own props.
//
// We don't ship React components here — those land in Phase 4b
// (spec 45 component-behavior). What we DO ship is the contract +
// the auto-dismiss rule constants the pattern requires.

/**
 * Empty state — surfaces with zero data should render this shape.
 * `primaryAction` is the recommended next step; `secondaryAction` is
 * optional escape hatch (e.g. "Watch a tour"). Per spec 43 AC: every
 * primary surface declares an empty state following this pattern.
 */
export interface EmptyStatePattern {
  readonly icon?: string;
  readonly illustrationSrc?: string;
  readonly heading: string;
  readonly body: string;
  readonly primaryAction?: { readonly label: string; readonly onActivate: string };
  readonly secondaryAction?: { readonly label: string; readonly onActivate: string };
}

/**
 * Loading state — skeleton-first per spec 43. `kind` discriminates
 * skeleton (default) vs spinner (only when skeletons would be
 * misleading) vs progress bar (known-duration operations).
 */
export type LoadingStatePattern =
  | { readonly kind: "skeleton"; readonly shape: "list" | "card" | "table" | "form" }
  | { readonly kind: "spinner"; readonly label: string }
  | { readonly kind: "progress"; readonly percent: number; readonly label: string };

/**
 * Toast severity — drives auto-dismiss timing per spec 43 AC.
 */
export type ToastSeverity = "info" | "success" | "warning" | "danger";

/**
 * Auto-dismiss durations (ms) per severity. `null` means "manual
 * dismiss only" — danger toasts NEVER auto-close because they may
 * carry an action the user must consciously acknowledge or dismiss.
 *
 * Spec 43 §feat_ds_pattern_toast_alert AC.
 */
export const TOAST_AUTO_DISMISS_MS: Readonly<Record<ToastSeverity, number | null>> = {
  info: 5000,
  success: 5000,
  warning: 8000,
  danger: null,
} as const;

export interface ToastPattern {
  readonly severity: ToastSeverity;
  readonly title: string;
  readonly body?: string;
  readonly action?: { readonly label: string; readonly onActivate: string };
  /** Hover/focus pause is required by the pattern; component handles it. */
}

/**
 * Resolve the auto-dismiss timeout for a given severity. Returns `null`
 * when manual dismissal is required.
 *
 * Helper exists so component code doesn't reach into the constant
 * record directly — caller intent (`autoDismissMs(severity)`) reads
 * better than `TOAST_AUTO_DISMISS_MS[severity]`.
 */
export function autoDismissMs(severity: ToastSeverity): number | null {
  return TOAST_AUTO_DISMISS_MS[severity];
}

/**
 * Form field — composition contract per spec 43 AC. Every field
 * carries a label + optional help text + error message tied via
 * aria-describedby. Required fields render a visible marker AND
 * carry aria-required.
 */
export interface FormFieldPattern {
  readonly id: string;
  readonly label: string;
  readonly helpText?: string;
  readonly errorMessage?: string;
  readonly required?: boolean;
  readonly optional?: boolean;
  readonly formatHint?: string;
}

/**
 * Build the `aria-describedby` token string for a form field. Returns
 * a space-separated list of element IDs that the field input should
 * point at. Empty when the field has no help text or error message.
 *
 * Pattern: `${id}-help ${id}-error`. Component authors generate sub-
 * element IDs with these suffixes.
 */
export function ariaDescribedByFor(field: FormFieldPattern): string | undefined {
  const parts: string[] = [];
  if (field.helpText !== undefined) parts.push(`${field.id}-help`);
  if (field.errorMessage !== undefined) parts.push(`${field.id}-error`);
  if (parts.length === 0) return undefined;
  return parts.join(" ");
}
